import { Button, PetitionCard, Text } from '@change/ds-core-ui/next';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';
// eslint-disable-next-line internal-rules/no-restricted-design-system-imports
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { SapToDm } from '../../SapToDm';

import { useTargetedPetitionListRedesign } from './hooks/useTargetedPetitionListRedesign';

export function TargetedPetitionListRedesign() {
	const {
		data: { showSapToDm, petitions, hasNextPage, nextPageLoading, sectionHeader },
		actions: { onClickPetitionLink, loadMore },
	} = useTargetedPetitionListRedesign();

	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	const { translate } = useI18n();

	if (!petitions.length) return null;

	return (
		<div data-qa="dm-petitions-content" ref={containerRef}>
			<Text as="h2" variant="heading" className="mb-6">
				{sectionHeader}
			</Text>
			{showSapToDm ? (
				<div className="grid grid-cols-1 gap-6 md:grid-cols-3 xl:grid-cols-4">
					<div className="col-span-1 flex justify-center">
						<PetitionCard
							key={petitions[0].id}
							imageUrl={petitions[0].photo?.petitionMedium.url}
							location={petitions[0].relevantLocationLocalizedName || ''}
							petitionId={petitions[0].id}
							petitionLinkOnClick={() => onClickPetitionLink(petitions[0].id)}
							petitionLinkUrl={`/p/${encodeURIComponent(petitions[0].slug)}`}
							signaturesLabel={petitions[0].signatureState.signatureCount.displayedLocalized}
							mediaMentionsLabel={
								petitions[0].mediaHitsConnection.totalCount > 0
									? petitions[0].mediaHitsConnection.totalCountLocalized
									: undefined
							}
							title={petitions[0].ask}
						/>
					</div>
					<div className="col-span-1 md:col-span-2 xl:col-span-3">
						<SapToDm />
					</div>
				</div>
			) : (
				<div className="flex justify-center">
					<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{petitions.map((petition, index) => (
							<div key={petition.id}>
								{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
									<div className="sr-only" role="alert">
										<TranslatePlural
											value="corgi.decision_maker_profile.petitions_list.fetched_more"
											count={petitions.length - lastEvent.previousItemsCount}
											replacements={{ count: petitions.length - lastEvent.previousItemsCount }}
										/>
									</div>
								)}

								<PetitionCard
									imageUrl={petition.photo?.petitionMedium.url}
									location={petition.relevantLocationLocalizedName || ''}
									petitionId={petition.id}
									petitionLinkOnClick={() => onClickPetitionLink(petition.id)}
									petitionLinkUrl={`/p/${encodeURIComponent(petition.slug)}`}
									signaturesLabel={petition.signatureState.signatureCount.displayedLocalized}
									mediaMentionsLabel={
										petition.mediaHitsConnection.totalCount > 0
											? petition.mediaHitsConnection.totalCountLocalized
											: undefined
									}
									title={petition.ask}
									ref={getListItemRef(index)}
								/>
							</div>
						))}
					</div>
				</div>
			)}

			{nextPageLoading && (
				<div className="mt-16">
					<div className="sr-only" role="alert">
						<Translate value="corgi.decision_maker_profile.petitions_list.fetching_more" />
					</div>
					<Loader size={44} mx="auto" ref={loadingRef} data-testid="load-more-btn" />
				</div>
			)}

			{hasNextPage && !nextPageLoading && (
				<div className="flex justify-center">
					<Button
						className="mt-16 basis-full md:basis-1/3"
						size="small"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: petitions.length });
							loadMore();
						}}
						data-qa="dm-petitions-load-more"
						ref={loadMoreRef}
					>
						{translate('fe.components.load_more.load_more')}
					</Button>
				</div>
			)}
		</div>
	);
}
